import React, { useState, useRef, useEffect } from "react";
import { AiOutlineFolder } from "react-icons/ai";
import { FiUser, FiChevronDown } from "react-icons/fi";
import { GoQuestion } from "react-icons/go";
import { RxDashboard } from "react-icons/rx";
import { Outlet, NavLink, Link } from "react-router-dom";
import { RiMenuFold4Line, RiMenuUnfold4Line } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from '../../../config'; // Adjust the path as necessary

const DashboardLayout = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(window.innerWidth >= 768);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const navItems = [
    {
      path: "/dashboard",
      icon: <RxDashboard size={18} />,
      label: "Dashboard",
    },
    {
      path: "/archive",
      icon: <AiOutlineFolder size={18} />,
      label: "Archive",
    },
    {
      path: "/qa",
      icon: <GoQuestion size={18} />,
      label: "Q & A",
    },
  ];

  // Handle click outside of dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      // Call Django logout endpoint
      await fetch(`${API_BASE_URL}/api/logout/`, {
        method: 'POST',
        credentials: 'include',
      });

      // Clear saved information
      localStorage.clear();
      sessionStorage.clear();
      document.cookie = 'csrftoken=; Max-Age=0; path=/; domain=' + window.location.hostname;

      // Redirect to home page
      navigate('/');
    } catch (error) {
      console.error("Failed to log out:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 text-white flex">
      {/* Sidebar */}
      <div
        className={`fixed w-64 public-nav min-h-screen transition-all duration-300 ease-in-out ${
          isMenuOpen ? "left-0 md:relative" : "-left-64"
        } z-50`}>
        {/* Logo section */}
        <div className="border-b border-gray-500 py-2">
          <img src="/imgs/logo.png" alt="TableTop" className="h-16 mx-auto" />
        </div>

        {/* Mobile Close Button */}
        <button
          className="absolute top-4 right-4 text-white"
          onClick={() => setIsMenuOpen(false)}>
          <RiMenuUnfold4Line size={24} />
        </button>

        {/* Navigation items */}
        <nav className="mt-8">
          <div className="px-4 space-y-2">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `w-full flex items-center space-x-3 px-4 py-2 rounded-lg ${
                    isActive
                      ? "bg-primary text-white"
                      : "text-gray-300 hover:bg-slate-700"
                  }`
                }>
                {item.icon}
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>
      </div>

      {/* Main content */}
      <div className="flex-1 ease-in-out">
        {/* Top navigation */}
        <header className="public-nav p-4">
          <div className="flex justify-between items-center">
            <button
              className={`text-white ${isMenuOpen ? "md:hidden" : ""}`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}>
              <RiMenuFold4Line size={24} />
            </button>

            {/* Profile dropdown */}
            <div className="relative ml-auto" ref={dropdownRef}>
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center space-x-2 text-white hover:text-gray-200 focus:outline-none">
                <FiUser size={24} />
                <FiChevronDown
                  size={20}
                  className={`transform transition-transform duration-200 ${
                    isProfileOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* Dropdown menu */}
              {isProfileOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50">
                  <div className="py-1">
                    <Link
                      to="/profile"
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Profile
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100">
                      Log Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content - rendered through Outlet */}
        <Outlet />
      </div>
    </div>
  );
};

export default DashboardLayout; 